import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater, type BundleInfo, type LatestVersion } from '@capgo/capacitor-updater';
import { Subject } from 'rxjs';
import { AppUpdateAvailableEvent, IAppUpdateAdapter } from './update-adapter';

const DEFAULT_CURRENT_HASH = 'native';

const isNoNewVersionError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  return error.message.includes('No new version available') || error.message.includes('no_new_version_available');
};

export class CapgoUpdateAdapter implements IAppUpdateAdapter {
  private readonly updateAvailableSubject = new Subject<AppUpdateAvailableEvent>();
  readonly updateAvailable$ = this.updateAvailableSubject.asObservable();

  private latestVersion: LatestVersion | null = null;
  private downloadedBundle: BundleInfo | null = null;

  constructor(private readonly getBaseUrl: () => Promise<string>) {}

  initialize(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    void this.doInitialize().catch((error: unknown) => {
      console.warn('[Capgo] initialization failed:', error);
    });
  }

  private async doInitialize(): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    if (baseUrl) {
      // Must await before getLatest() — native side reads the URL synchronously from its own state.
      await CapacitorUpdater.setUpdateUrl({ url: `${baseUrl}/capgoUpdateMetadata` }).catch((error: unknown) => {
        console.warn('[Capgo] setUpdateUrl failed:', error);
      });
    }

    void CapacitorUpdater.notifyAppReady().catch((error: unknown) => {
      console.warn('[Capgo] notifyAppReady failed:', error);
    });

    await this.checkForUpdate().catch((error: unknown) => {
      console.warn('[Capgo] update check failed:', error);
    });
  }

  async checkForUpdate(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      const latest = await CapacitorUpdater.getLatest();
      if (!latest.url || !latest.version) {
        return false;
      }

      const current = await CapacitorUpdater.current();
      if (current.bundle.version === latest.version) {
        return false;
      }

      this.latestVersion = latest;
      this.downloadedBundle = null;
      this.updateAvailableSubject.next({
        currentHash: current.bundle.version || DEFAULT_CURRENT_HASH,
        newHash: latest.version,
        appData: {
          name: 'Personal Finance',
          version: latest.version,
          buildTime: '',
          releaseNotes: latest.comment ?? '',
        },
      });

      return true;
    } catch (error) {
      if (isNoNewVersionError(error)) {
        return false;
      }
      throw error;
    }
  }

  async downloadUpdate(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    if (!this.latestVersion) {
      const hasUpdate = await this.checkForUpdate();
      if (!hasUpdate || !this.latestVersion) {
        return;
      }
    }

    const { url, version, checksum, sessionKey, manifest } = this.latestVersion;
    if (!url) {
      return;
    }

    this.downloadedBundle = await CapacitorUpdater.download({
      url,
      version,
      checksum,
      sessionKey,
      manifest,
    });
  }

  async applyUpdate(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    if (!this.downloadedBundle) {
      await this.downloadUpdate();
    }

    if (!this.downloadedBundle) {
      return;
    }

    await CapacitorUpdater.next({ id: this.downloadedBundle.id });
  }

  restartApp(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return Promise.resolve();
    }
    return CapacitorUpdater.reload();
  }
}
